/**
 * Logging service for tracking user actions and system events
 * Quick integration with existing Supabase infrastructure
 */

import { SupabaseClient } from '@supabase/supabase-js';

export type LogSeverity = 'info' | 'warning' | 'error' | 'success';
export type LogCategory =
  | 'user'
  | 'system'
  | 'homework'
  | 'class'
  | 'auth'
  | 'file';

export interface LogEntry {
  id?: string;
  timestamp?: string;
  user_email?: string;
  user_id?: string;
  action: string;
  description: string;
  category: LogCategory;
  severity: LogSeverity;
  details?: any;
  ip_address?: string;
  user_agent?: string;
}

class Logger {
  private supabase: SupabaseClient | null = null;
  private currentUser: any = null;

  // Initialize the logger with Supabase client and user context
  initialize(supabase: SupabaseClient, user?: any) {
    this.supabase = supabase;
    this.currentUser = user;

    // Create initial log entries if table is empty (for testing)
    this.createInitialLogs();
  }

  // Create initial log entries for demonstration
  private async createInitialLogs() {
    if (!this.supabase) return;

    try {
      // Check if table exists and has data
      const { count } = await this.supabase
        .from('system_logs')
        .select('*', { count: 'exact', head: true });

      if (count === 0) {
        // Create some initial log entries
        const initialLogs = [
          {
            user_email: 'admin@grcs.com',
            action: 'System Startup',
            description: 'Logging system initialized successfully',
            category: 'system',
            severity: 'success',
            details: JSON.stringify({ version: '1.0.0' }),
          },
          {
            user_email: 'admin@grcs.com',
            action: 'User Login',
            description: 'Administrator logged into the system',
            category: 'auth',
            severity: 'info',
            details: JSON.stringify({ loginMethod: 'email' }),
          },
          {
            user_email: 'System',
            action: 'Database Connection',
            description: 'Successfully connected to database',
            category: 'system',
            severity: 'success',
            details: JSON.stringify({ connectionTime: '150ms' }),
          },
        ];

        await this.supabase.from('system_logs').insert(initialLogs);
      }
    } catch (error) {
      console.warn(
        'Could not create initial logs (table may not exist):',
        error,
      );
    }
  }

  // Generic log method
  private async log(
    action: string,
    description: string,
    category: LogCategory,
    severity: LogSeverity,
    details?: any,
  ) {
    if (!this.supabase) {
      console.warn('Logger not initialized with Supabase client');
      return;
    }

    try {
      const logEntry: LogEntry = {
        timestamp: new Date().toISOString(),
        user_email: this.currentUser?.email || 'System',
        user_id: this.currentUser?.id || undefined,
        action,
        description,
        category,
        severity,
        details: details ? JSON.stringify(details) : undefined,
        ip_address: undefined, // Could be enhanced with IP detection
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      };

      // Store in a logs table (we'll create this)
      const { error } = await this.supabase
        .from('system_logs')
        .insert([logEntry]);

      if (error) {
        console.error('Failed to store log:', error);
        // Fallback to console logging
        console.log(
          `[${severity.toUpperCase()}] ${action}: ${description}`,
          details,
        );
      }
    } catch (error) {
      console.error('Logger error:', error);
      // Fallback to console logging
      console.log(
        `[${severity.toUpperCase()}] ${action}: ${description}`,
        details,
      );
    }
  }

  // Convenience methods for different log levels
  async info(
    action: string,
    description: string,
    category: LogCategory = 'system',
    details?: any,
  ) {
    return this.log(action, description, category, 'info', details);
  }

  async success(
    action: string,
    description: string,
    category: LogCategory = 'system',
    details?: any,
  ) {
    return this.log(action, description, category, 'success', details);
  }

  async warning(
    action: string,
    description: string,
    category: LogCategory = 'system',
    details?: any,
  ) {
    return this.log(action, description, category, 'warning', details);
  }

  async error(
    action: string,
    description: string,
    category: LogCategory = 'system',
    details?: any,
  ) {
    return this.log(action, description, category, 'error', details);
  }

  // Specific logging methods for common actions
  async logUserLogin(email: string, success: boolean, details?: any) {
    const action = success ? 'Login Success' : 'Login Failed';
    const description = `User ${email} ${
      success ? 'logged in successfully' : 'failed to log in'
    }`;
    const severity: LogSeverity = success ? 'success' : 'warning';
    return this.log(action, description, 'auth', severity, details);
  }

  async logUserCreated(
    email: string,
    role: string,
    createdBy?: string,
    details?: any,
  ) {
    const action = 'User Created';
    const description = `New ${role} account created for ${email}${
      createdBy ? ` by ${createdBy}` : ''
    }`;
    return this.log(action, description, 'user', 'success', {
      email,
      role,
      createdBy,
      ...details,
    });
  }

  async logHomeworkPublished(
    title: string,
    grade: string,
    assignedTo?: number,
    details?: any,
  ) {
    const action = 'Homework Published';
    const description = `Published homework "${title}" for Grade ${grade}${
      assignedTo ? ` (${assignedTo} students)` : ''
    }`;
    return this.log(action, description, 'homework', 'info', {
      title,
      grade,
      assignedTo,
      ...details,
    });
  }

  async logClassCreated(
    className: string,
    grade: string,
    capacity?: number,
    details?: any,
  ) {
    const action = 'Class Created';
    const description = `Created new class "${className}" for Grade ${grade}${
      capacity ? ` (capacity: ${capacity})` : ''
    }`;
    return this.log(action, description, 'class', 'success', {
      className,
      grade,
      capacity,
      ...details,
    });
  }

  async logFileUpload(
    fileName: string,
    fileSize: number,
    uploadType: string,
    details?: any,
  ) {
    const action = 'File Uploaded';
    const description = `Uploaded ${uploadType} file: ${fileName} (${(
      fileSize /
      1024 /
      1024
    ).toFixed(2)} MB)`;
    return this.log(action, description, 'file', 'info', {
      fileName,
      fileSize,
      uploadType,
      ...details,
    });
  }

  async logSystemError(
    errorMessage: string,
    component?: string,
    details?: any,
  ) {
    const action = 'System Error';
    const description = `Error in ${component || 'system'}: ${errorMessage}`;
    return this.log(action, description, 'system', 'error', {
      component,
      errorMessage,
      ...details,
    });
  }

  async logDatabaseOperation(
    operation: string,
    table: string,
    recordId?: string,
    details?: any,
  ) {
    const action = `Database ${operation}`;
    const description = `${operation} operation on ${table}${
      recordId ? ` (ID: ${recordId})` : ''
    }`;
    return this.log(action, description, 'system', 'info', {
      operation,
      table,
      recordId,
      ...details,
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience function to initialize logger
export const initializeLogger = (supabase: SupabaseClient, user?: any) => {
  logger.initialize(supabase, user);
};
